import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionInfo {
  tier: string;
  status: string;
  subscription_end_date: string | null;
  polar_subscription_id: string | null;
  polar_customer_id: string | null;
  is_founders_club: boolean;
}

export interface SubscriptionTier {
  id: 'pro' | 'solo';
  name: string;
  price: number;
  credits: number;
  overage: string;
  seats: number;
  isWeekly?: boolean;
  features: string[];
  description: string;
  campaign?: string; // Optional campaign/discount ID
  billingPeriod?: 'week' | 'month'; // Billing period for display
}

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'solo',
    name: 'Starter',
    price: 15,
    credits: 1000,
    overage: '$0.50 per additional 100 credits',
    seats: 1,
    billingPeriod: 'month',
    description: 'Sufficient for most founders',
    features: [
      '1000 Credits',
      'All features',
      'Monthly reset',
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 39,
    credits: 4000,
    overage: '$0.45 per additional 100 credits',
    seats: 1,
    campaign: 'early_adopter',
    billingPeriod: 'month',
    description: 'Best for heavy iterations and volume',
    features: [
      '4000 Credits',
      'All features',
      'Monthly reset',
    ]
  },
];

/**
 * Campaign/Discount configuration
 * Add campaign IDs here to apply discounts to specific tiers
 */
export const CAMPAIGNS: Record<string, {
  discount_id: string;
  description: string;
  isGlobal?: boolean;
}> = {
  'launch_campaign': {
    discount_id: 'bafb5a9f-233f-4e55-8a59-df76b347ebe9',
    description: 'Launch Special: 20% Off Any Plan for 3 Months',
    isGlobal: true
  },
  'early_adopter': {
    discount_id: 'c59cd1b8-7359-4b95-a744-72721519dbb2',
    description: 'Early Lifetime Discount',
    isGlobal: false
  }
};

/**
 * Calculate discounted price for a tier (without Polar API call)
 * This is a fallback that doesn't fetch real-time discount data
 * Use getDiscountedPriceWithPolar for accurate pricing
 */
export function getDiscountedPrice(tier: SubscriptionTier): {
  originalPrice: number;
  discountedPrice: number | null;
  hasDiscount: boolean;
} {
  // For now, just return no discount since we'll use Polar API
  return {
    originalPrice: tier.price,
    discountedPrice: null,
    hasDiscount: false
  };
}

/**
 * Calculate discounted price using actual Polar discount data
 * @param tier - Subscription tier
 * @param polarDiscount - Polar discount object from API
 */
export function calculateDiscountedPrice(
  tier: SubscriptionTier,
  polarDiscount: { type: 'fixed' | 'percentage'; amount?: number; basis_points?: number; basisPoints?: number } | null
): {
  originalPrice: number;
  discountedPrice: number | null;
  hasDiscount: boolean;
} {

  if (!polarDiscount) {
    return {
      originalPrice: tier.price,
      discountedPrice: null,
      hasDiscount: false
    };
  }

  let discountedPrice: number;

  // Handle both camelCase (from SDK) and snake_case (from raw API)
  const basisPoints = (polarDiscount as any).basisPoints ?? polarDiscount.basis_points;

  if (polarDiscount.type === 'percentage' && basisPoints !== undefined) {
    // Polar uses basis points: 10000 = 100%, 2500 = 25%
    const discountPercentage = basisPoints / 10000;
    discountedPrice = tier.price * (1 - discountPercentage);
  } else if (polarDiscount.type === 'fixed' && polarDiscount.amount !== undefined) {
    // Polar amount is in cents
    const discountDollars = polarDiscount.amount / 100;
    discountedPrice = tier.price - discountDollars;
  } else {
    return {
      originalPrice: tier.price,
      discountedPrice: null,
      hasDiscount: false
    };
  }

  // Ensure price doesn't go below 0
  discountedPrice = Math.max(0, discountedPrice);

  const result = {
    originalPrice: tier.price,
    discountedPrice: Math.round(discountedPrice * 100) / 100, // Round to 2 decimals
    hasDiscount: true
  };
  return result;
}

/**
 * Get user's current subscription info from profiles
 */
export async function getCurrentSubscription(): Promise<SubscriptionInfo | null> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.user) {
    return null;
  }
  const user = session.user;

  const { data: profile } = await supabase
    .from('profiles')
    .select('subscription_tier, subscription_status, subscription_end_date, polar_subscription_id, polar_customer_id, is_founders_club')
    .eq('user_id', user.id)
    .single();

  if (!profile) {
    return null;
  }

  return {
    tier: profile.subscription_tier || 'free',
    status: profile.subscription_status || 'onboarding',
    subscription_end_date: profile.subscription_end_date,
    polar_subscription_id: profile.polar_subscription_id,
    polar_customer_id: profile.polar_customer_id,
    is_founders_club: !!profile.is_founders_club,
  };
}

/**
 * Check if user can use AI features
 */
export async function canUseAI(): Promise<{ allowed: boolean; reason?: string }> {
  const subscription = await getCurrentSubscription();

  if (!subscription) {
    return {
      allowed: false,
      reason: 'Sign in to continue.'
    };
  }

  if (subscription.is_founders_club) {
    return { allowed: true };
  }

  if (subscription.tier === 'pro_byok') {
    return { allowed: true };
  }

  const hasPaidTier = ['weekly', 'solo', 'pro', 'pro_byok'].includes(subscription.tier);
  const hasPaidAccessStatus = subscription.status === 'active' || subscription.status === 'trialing';

  if (hasPaidTier && hasPaidAccessStatus) {
    return { allowed: true };
  }

  if (subscription.status === 'trialing' || subscription.status === 'onboarding') {
    return {
      allowed: false,
      reason: 'Start your free trial or subscribe to begin chatting.'
    };
  }

  return {
    allowed: false,
    reason: 'Your subscription is not active. Please upgrade to continue using AI features.'
  };
}
